import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type { InlineQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseInlineQuizOptions {
  quizId: string | number
  quiz: InlineQuizData
  onSolve?: (
    quizId: string | number,
    answers: string[][],
    vars: Record<string, unknown>,
    elapsedTime: number,
  ) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[][]
  onAnswerChange?: (answers: string[][]) => void
}

export function useInlineQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseInlineQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  const children = quiz.children ?? []
  const childCount = children.length

  const correctAnswersByChild = useMemo<string[][]>(() => {
    const raw = solveResult?.correctAnswers as unknown
    if (Array.isArray(raw) && raw.length === childCount && Array.isArray(raw[0])) {
      return raw as string[][]
    }
    return children.map(() => [])
  }, [solveResult?.correctAnswers, childCount, children])

  const correctsByChild = useMemo<boolean[][]>(() => {
    const raw = solveResult?.corrects as unknown
    if (Array.isArray(raw) && raw.length === childCount && Array.isArray(raw[0])) {
      return raw as boolean[][]
    }
    return children.map(() => [])
  }, [solveResult?.corrects, childCount, children])

  const computedInitialKeys = useMemo<string[][]>(() => {
    if (initialAnswersProp !== undefined) {
      return children.map((_, index) => initialAnswersProp[index] ?? [])
    }
    if (isViewSolved && correctAnswersByChild.length === childCount) {
      return correctAnswersByChild
    }
    return children.map(() => [])
  }, [initialAnswersProp, isViewSolved, correctAnswersByChild, childCount, children])

  const [selectedKeysByChild, setSelectedKeysByChild] = useState<string[][]>(computedInitialKeys)

  useEffect(() => {
    setSelectedKeysByChild(prev => {
      if (
        prev.length === computedInitialKeys.length &&
        prev.every((arr, i) =>
          arr.length === computedInitialKeys[i].length &&
          arr.every((v, j) => v === computedInitialKeys[i][j])
        )
      ) {
        return prev
      }
      return computedInitialKeys
    })
  }, [computedInitialKeys])

  const lastEmittedRef = useRef<string[][] | null>(null)
  useEffect(() => {
    const last = lastEmittedRef.current
    if (
      last &&
      last.length === selectedKeysByChild.length &&
      last.every((arr, i) =>
        arr.length === selectedKeysByChild[i].length &&
        arr.every((v, j) => v === selectedKeysByChild[i][j])
      )
    ) {
      return
    }
    lastEmittedRef.current = selectedKeysByChild
    onAnswerChange?.(selectedKeysByChild)
  }, [selectedKeysByChild, onAnswerChange])

  const resolvedCorrectKeySetByChild = useMemo<Set<string>[]>(() => {
    return children.map((child, idx) => {
      const set = new Set<string>()
      const provided = correctAnswersByChild[idx]
      if (provided && provided.length > 0) {
        const allKeys = child.choices.map(c => c.key)
        provided.forEach(ca => {
          if (allKeys.includes(ca)) {
            set.add(ca)
            return
          }
          const posIdx = parseInt(ca, 10)
          if (!isNaN(posIdx) && posIdx >= 1 && posIdx <= child.choices.length) {
            set.add(child.choices[posIdx - 1].key)
          }
        })
      }
      if (set.size === 0) {
        child.choices.filter(c => c.correct).forEach(c => set.add(c.key))
      }
      return set
    })
  }, [children, correctAnswersByChild])

  const perChildCorrectsBySelectedKey = useMemo<Array<Map<string, boolean>>>(() => {
    return children.map((_child, idx) => {
      const map = new Map<string, boolean>()
      const submitted = (initialAnswersProp?.[idx]) ?? selectedKeysByChild[idx] ?? []
      const childCorrects = correctsByChild[idx] ?? []
      submitted.forEach((key, i) => {
        if (i < childCorrects.length) map.set(key, childCorrects[i])
      })
      return map
    })
  }, [children, initialAnswersProp, selectedKeysByChild, correctsByChild])

  const perChildCorrect = useMemo<boolean[]>(() => {
    return children.map((_child, idx) => {
      const childCorrects = correctsByChild[idx] ?? []
      if (childCorrects.length === 0) return false
      return childCorrects.every(Boolean)
    })
  }, [children, correctsByChild])

  const totalCorrectCount = useMemo(() => perChildCorrect.filter(Boolean).length, [perChildCorrect])
  const totalChildCount = childCount

  const canSubmit = useMemo(() => {
    if (isSolved || isViewSolved) return false
    if (childCount === 0) return false
    return selectedKeysByChild.every(arr => arr.length > 0)
  }, [isSolved, isViewSolved, childCount, selectedKeysByChild])

  const handleSelectChild = useCallback(
    (childIdx: number, key: string) => {
      if (isSolved || isViewSolved) return
      setSelectedKeysByChild(prev => {
        const next = prev.map(arr => arr.slice())
        const child = children[childIdx]
        if (!child) return prev
        const current = next[childIdx] ?? []

        if (current.includes(key)) {
          next[childIdx] = current.filter(k => k !== key)
          return next
        }
        next[childIdx] = [...current, key]
        return next
      })
    },
    [isSolved, isViewSolved, children],
  )

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, selectedKeysByChild, quiz.vars, elapsedTime)
  }, [canSubmit, isSolved, startTime, onSolve, quizId, selectedKeysByChild, quiz.vars])

  return {
    children,
    selectedKeysByChild,
    isSolved,
    canSubmit,
    startTime,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    shouldShowCorrectAnswers,
    isViewSolved,
    resolvedCorrectKeySetByChild,
    perChildCorrectsBySelectedKey,
    perChildCorrect,
    totalCorrectCount,
    totalChildCount,
    handleSelectChild,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
  }
}
